import React, { ReactNode } from 'react';

interface DrawerToggleButtonProps {
  position: 'start' | 'end' | 'left' | 'right' | 'up' | 'down';
  sx?: React.CSSProperties;
  onClick: () => void;
  children?: ReactNode;
}

const DrawerToggleButton: React.FC<DrawerToggleButtonProps> = ({
  position,
  sx = {},
  onClick,
  children
}) => {
  // Base styles for the toggle button
  const getBaseStyle = (): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      position: "fixed",
      zIndex: 1300,
      backgroundColor: "var(--tasks-group-bg, #fff)",
      border: "1px solid var(--border-color, #e0e0e0)",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
      transition: "all 0.3s ease",
      width: "48px",
      height: "48px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
    };

    // Position-specific styles
    switch (position) {
      case 'start':
        return {
          ...baseStyle,
          insetInlineStart: "0px",
          top: "50%",
          transform: "translateY(-50%)",
          borderStartStartRadius: "0",
          borderStartEndRadius: "24px",
          borderEndStartRadius: "0", 
          borderEndEndRadius: "24px",
          borderInlineStart: "none",
          borderInlineEnd: "1px solid var(--border-color, #e0e0e0)",
        };
      case 'end':
        return {
          ...baseStyle,
          insetInlineEnd: "0px",
          top: "50%",
          transform: "translateY(-50%)",
          borderStartStartRadius: "24px",
          borderStartEndRadius: "0",
          borderEndStartRadius: "24px",
          borderEndEndRadius: "0",
          borderInlineEnd: "none",
          borderInlineStart: "1px solid var(--border-color, #e0e0e0)",
        };
      case 'left':
        return {
          ...baseStyle,
          left: "0px",
          top: "50%",
          transform: "translateY(-50%)",
          borderRadius: "0 24px 24px 0",
          borderLeft: "none",
          borderRight: "1px solid var(--border-color, #e0e0e0)",
        };
      case 'right':
        return {
          ...baseStyle,
          right: "0px",
          top: "50%",
          transform: "translateY(-50%)",
          borderRadius: "24px 0 0 24px",
          borderRight: "none",
          borderLeft: "1px solid var(--border-color, #e0e0e0)",
        };
      case 'up':
        return {
          ...baseStyle,
          top: "0px",
          left: "50%",
          transform: "translateX(-50%)",
          borderRadius: "0 0 24px 24px",
          borderTop: "none",
          borderBottom: "1px solid var(--border-color, #e0e0e0)",
        };
      case 'down':
        return {
          ...baseStyle,
          bottom: "0px",
          left: "50%",
          transform: "translateX(-50%)",
          borderRadius: "24px 24px 0 0",
          borderBottom: "none",
          borderTop: "1px solid var(--border-color, #e0e0e0)",
        };
      default:
        return {
          ...baseStyle,
          insetInlineEnd: "0px",
          top: "50%",
          transform: "translateY(-50%)",
          borderStartStartRadius: "24px",
          borderStartEndRadius: "0",
          borderEndStartRadius: "24px",
          borderEndEndRadius: "0",
          borderInlineEnd: "none",
          borderInlineStart: "1px solid var(--border-color, #e0e0e0)",
        };
    }
  };

  // Merge position-specific styles with custom sx styles
  const finalStyles = {
    ...getBaseStyle(),
    ...sx, // Allow custom styles to override position-specific styles
  };

  // Default hamburger menu icon if no children provided
  const defaultIcon = (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="3" y1="6" x2="21" y2="6"></line>
      <line x1="3" y1="12" x2="21" y2="12"></line>
      <line x1="3" y1="18" x2="21" y2="18"></line>
    </svg>
  );

  return (
    <div
      onClick={onClick}
      style={finalStyles}
      aria-label="Toggle drawer"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick();
        }
      }}
    >
      {children || defaultIcon}
    </div>
  );
};

export default DrawerToggleButton;
